import { Grid } from "@material-ui/core";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import RxBlobImageViewer from "../../components/RxBlobImageViewer";
import RxPaper from "../../components/RxPaper";
import RxProductActionContent from "../../components/RxProductActionContent";
import { AddCartClickProps } from "../../components/RxProductActionContent/RxProductActionContent";
import RxProductContainer from "../../components/RxProductContainer";
import RxProductContent from "../../components/RxProductContent";
import RxShoppingCart from "../../components/RxShoppingCart";
import RxTitleSeperator from "../../components/RxTitleSeperator";
import api from "../../config/AxiosConfig";
import { ProductCategoryObject } from "../../constants/CommonObject";
import { setCart } from "../../redux/slices/cart-slice";
import { store } from "../../redux/store";
import { isNullOrUndefined } from "../../utils/CommonFunctions";
import styles from "./SalePage.module.css";

interface ProductResultObject {
	category: ProductCategoryObject;
	products: ProductObject[];
	licences: LicenceInfoObject[];
}

export interface ProductObject {
	id: number;
	name: string;
	image: Blob;
	description: string;
	parentProductSelectable: boolean;
	maxPurchasableProductNumber: number;
	minPurchasableProductNumber: number;
	couponId: number;

	kdv: number;
	discount: number;
	showKdvIncluded: boolean;
	price: ProductPriceObject;
	priceWithKdv: ProductPriceObject;
}

export interface ProductPriceObject {
	price: number;
	additionPrice: number;
	priceWithCoupon: number;
}

export interface ProductBasketObject {
	productBasketId: number;
	parentProductSelectable: boolean;
	quantity: number;
	productCategoryId: number;
	productCategoryName: string;
	productId: number;
	productName: string;
	productImage: Blob;
	productDescription: string;
	productLicence: string;
	productPrice: number;
	productKdv: number;
	couponValue: number;
	couponCode: string;
	isCouponPercentDiscount: boolean;
	minNumberOfProduct: number;
	maxNumberOfProduct: number;
	productAdditionPrice: number;
	productBasketPrice: number;
	productBasketDiscount: number;
	productBasketTotalPrice: number;
	productCategoryType: string;
	showingCoupon: boolean;
}

export interface LicenceInfoObject {
	id: number;
	serialNumber: string;
	licenceYear: string;
	version: string;
	numberOfLicence: number;
	numberOfUser: number;
	numberOfMobile: number;
}

export interface DistributorObject {
	name: string;
	code: string;
}

export interface RxShoppingCartObject {
	id: number;
	installment: boolean;
	products: ProductBasketObject[];
	licences: LicenceInfoObject[];
	oldDistributor: DistributorObject | undefined;
	distributor: DistributorObject | undefined;
	totalDiscount: number;
	totalPrice: number;
	totalPriceWithKdv: number;
}

const SalePage = () => {
	const { categoryId }: any = useParams();

	const [refreshShoppingCart, setRefreshShoppingCart] = useState(false);
	const [productInfo, setProductInfo] = useState<ProductResultObject>();

	const setShoppingCart = (shoppingCart: RxShoppingCartObject | undefined) => {
		store.dispatch(
			setCart({
				cartItem: shoppingCart,
			})
		);
	};

	const getProducts = async () => {
		await api
			.get("/1/products/category/" + categoryId)
			.then((response: ProductResultObject) => {
								setProductInfo(response);
			});
	};

	const getBasketInfo = async () => {
		await api.get("/1/basket").then((response: RxShoppingCartObject) => {
			setShoppingCart(response);
		});
	};

	useEffect(() => {
		getProducts();
	}, []); // eslint-disable-line

	useEffect(() => {
		if (refreshShoppingCart) {
			getBasketInfo();
			setRefreshShoppingCart(false);
		}
	}, [refreshShoppingCart]); // eslint-disable-line

	const onAddCartClick = async (properties: AddCartClickProps) => {
		const requestLicence =
			properties.licenceEys ??
			(properties.licence === "Yeni Lisans" ? null : properties.licence);
		await api
			.post("/1/basket", {
				productId: properties.productId,
				quantity: properties.quantity,
				licence:
					properties.licenceEys && properties.licenceShouldSendNull
						? null
						: requestLicence,
				couponId: properties.couponId,
			})
			.then((response: RxShoppingCartObject) => {
				setShoppingCart(response);
			})
			.catch((error) => {
				setRefreshShoppingCart(true);
			});
	};

	function calculatePrice(
		price: ProductPriceObject,
		priceWithKdv: ProductPriceObject,
		kdv: number,
		withDiscount: boolean
	): string {
		const displayPrice = withDiscount ? price.priceWithCoupon : price.price;
		const displayPriceWithKdv = withDiscount
			? priceWithKdv.priceWithCoupon
			: priceWithKdv.price;

		return (
			displayPrice?.toFixed(2) +
			" TL + KDV(%" +
			kdv?.toFixed(2) +
			") = " +
			displayPriceWithKdv?.toFixed(2) +
			" TL"
		);
	}

	return (
		<>
			{productInfo && (
				<div className={styles.displayFlex}>
					<RxPaper
						className={
							styles.mainPaper + " " + styles.mainPaperRightMargin
						}
					>
						<Grid
							container
							spacing={1}
							direction="row"
							justifyContent="center"
							alignItems="center"
							alignContent="center"
						>
							<Grid item xs={12} sm={12}>
								<Grid container spacing={1}>
									<Grid item xs={12} sm={12}>
										<Grid container spacing={1}>
											<Grid item xs={12} sm={12}>
												<RxTitleSeperator
													titleLabel={
														productInfo?.category?.name +
														" İçin Ürünler"
													}
													className={styles.seperatorLineBottom}
												/>
											</Grid>
										</Grid>
										<Grid container spacing={5}>
											{productInfo?.products?.map((item, index) => {
												const priceString = calculatePrice(
													item.price,
													item.priceWithKdv,
													item.kdv,
													false
												);
												const discountString = !isNullOrUndefined(
													item.price.priceWithCoupon
												)
													? calculatePrice(
															item.price,
															item.priceWithKdv,
															item.kdv,
															true
													  )
													: undefined;
												return (
													<Grid
														item
														xs={12}
														sm={10}
														md={8}
														lg={6}
														xl={4}
														key={"grid_" + index}
													>
														<RxProductContainer
															key={"rxpc_" + index}
															className={styles.productContainer}
															children={
																<RxProductContent
																	key={"rxpc_content_" + index}
																	title={item.name}
																	description={
																		item.description
																	}
																	kdvIncluded={
																		item.showKdvIncluded
																	}
																	priceInfo={
																		item.price.additionPrice
																			? undefined
																			: priceString
																	}
																	discountInfo={
																		item.price.additionPrice
																			? undefined
																			: discountString
																	}
																	priceDetail={
																		item.price.additionPrice
																			? {
																					price: item.showKdvIncluded
																						? item
																								.priceWithKdv
																								.price
																						: item.price
																								.price,
																					additionPrice:
																						item.showKdvIncluded
																							? item
																									.priceWithKdv
																									.additionPrice
																							: item
																									.price
																									.additionPrice,
																					minNumberOfProduct:
																						item.minPurchasableProductNumber,
																					maxNumberOfProduct:
																						item.maxPurchasableProductNumber,
																			  }
																			: undefined
																	}
																	discountDetail={
																		item.price.additionPrice
																			? {
																					price: item.showKdvIncluded
																						? item
																								.priceWithKdv
																								.priceWithCoupon
																						: item.price
																								.priceWithCoupon,
																					additionPrice:
																						item.showKdvIncluded
																							? item
																									.priceWithKdv
																									.additionPrice
																							: item
																									.price
																									.additionPrice,
																					minNumberOfProduct:
																						item.minPurchasableProductNumber,
																					maxNumberOfProduct:
																						item.maxPurchasableProductNumber,
																					discount:
																						item.discount,
																			  }
																			: undefined
																	}
																	actionComponent={
																		<RxProductActionContent
																			key={
																				"rxpc_action_" +
																				index
																			}
																			productId={item.id}
																			maxNumberOfProduct={
																				item.maxPurchasableProductNumber
																			}
																			minNumberOfProduct={
																				item.minPurchasableProductNumber
																			}
																			parentProductSelectable={
																				item.parentProductSelectable
																			}
																			licences={
																				productInfo.licences
																			}
																			couponId={
																				item.couponId
																			}
																			onAddCartClick={
																				onAddCartClick
																			}
																			categoryType={
																				productInfo.category
																					.type
																			}
																		/>
																	}
																	image={
																		item.image && (
																			<RxBlobImageViewer
																				key={
																					"rxpc_image_" +
																					index
																				}
																				image={item.image}
																				alt="user_settings"
																				height="128px"
																			/>
																		)
																	}
																/>
															}
														/>
													</Grid>
												);
											})}
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} sm={12}></Grid>
										</Grid>
										<Grid container spacing={1}>
											<Grid item xs={12} sm={12}></Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</RxPaper>
					<RxShoppingCart className={styles.shoppingCart} />
				</div>
			)}
		</>
	);
};

export default SalePage;
